import type { RunProgress } from './lib/progress';
import { formatNumberRo } from '@/lib/format';

/**
 * DESIGN.md „Verdict prose": a results page opens with ONE sentence stating
 * the outcome in Romanian, numbers bolded and status-coloured. Its absence is
 * why the page could previously tell two different stories about the same run.
 */
export function VerdictLine({ progress }: { progress: RunProgress }) {
  const { validated, classified } = progress;
  const n = (v: number, tone: string) => (
    <strong className={`font-semibold tabular-nums ${tone}`}>{formatNumberRo(v)}</strong>
  );
  /**
   * Romanian past-participle-as-adjective agreement with the neuter noun
   * „rând" (singular agrees like masculine, plural like feminine) — n=1 is a
   * realistic case (a one-row test upload), not just a large-N default that
   * can hardcode the plural.
   */
  const row = (v: number) => (v === 1 ? 'rând' : 'rânduri');
  const clasificat = (v: number) => (v === 1 ? 'clasificat' : 'clasificate');
  const validat = (v: number) => (v === 1 ? 'validat' : 'validate');

  if (classified === 0) {
    return <p className="text-[15px] text-ink-2">Niciun rând nu a fost clasificat încă.</p>;
  }
  if (validated === 0) {
    return (
      <p className="text-[15px] text-ink-2">
        {n(classified, 'text-ink')} {row(classified)} {clasificat(classified)} — niciun rând validat încă.
      </p>
    );
  }
  if (validated >= classified) {
    if (classified === 1) {
      return <p className="text-[15px] text-ink-2">Rândul clasificat a fost validat.</p>;
    }
    return (
      <p className="text-[15px] text-ink-2">
        Toate cele {n(classified, 'text-ok')} rânduri clasificate au fost validate.
      </p>
    );
  }
  return (
    <p className="text-[15px] text-ink-2">
      {n(validated, 'text-ok')} {row(validated)} {validat(validated)} din {n(classified, 'text-ink')}{' '}
      {clasificat(classified)}.
    </p>
  );
}
